import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import RamblaLogoWhite from 'assets/img/rambla-logo.png';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Container, Nav } from 'react-bootstrap';
import { matchPath, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { breakpoints } from 'utils/rwd';
import {
  BurgerMenuButton,
  NavigationLinkLabel,
  NavigationLogo,
  NavigationWrapper,
  ToggleMenuButton,
} from './styles';

export const NavigationContext = createContext();

const matchesActivePath = (activePath, pathname) => {
  if (!activePath) return false;

  if (typeof activePath === 'string') {
    return Boolean(matchPath({ path: activePath, end: false }, pathname));
  }

  return Boolean(matchPath({ path: activePath.path, end: activePath.end ?? false }, pathname));
};

const matchesNavigationItem = (item, pathname) => {
  if (item.link === pathname) {
    return true;
  }

  if (item.link && pathname.startsWith(`${item.link}/`)) {
    return true;
  }

  return item.activePaths?.some((activePath) => matchesActivePath(activePath, pathname));
};

const Navigation = ({
  compact,
  toggleNavigation,
  profileSwitcherOpened,
  children,
  items: navigationItems,
  ...restProps
}) => {
  const location = useLocation();
  const [isVisible, setVisible] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  useEffect(() => {
    const findActiveSection = (items) =>
      items.reduce((found, item) => {
        if (found) {
          return found;
        }

        if (matchesNavigationItem(item, location.pathname)) {
          return item.id;
        }

        if (item.items?.length) {
          const nestedFound = findActiveSection(item.items);
          if (nestedFound) return nestedFound;
        }

        return null;
      }, null);

    const currentActiveSection = findActiveSection(navigationItems);
    setActiveSection(currentActiveSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationItems, location.pathname]);

  const handleBurgerMenuClick = () => {
    if (window.matchMedia(`(max-width: ${breakpoints.sm})`)) {
      toggleNavigation(false);
      setVisible(!isVisible);
    }
  };

  return (
    <NavigationContext.Provider value={{ compact, activeSection, setActiveSection }}>
      <NavigationWrapper ref={navRef} $compact={compact} {...restProps}>
        <Container fluid>
          <BurgerMenuButton onClick={handleBurgerMenuClick}>
            <span className="navbar-toggler-icon" />
          </BurgerMenuButton>
          <NavigationLogo $compact={compact} src={RamblaLogoWhite} />
          <div className={`collapse navbar-collapse ${isVisible && 'show'}`}>
            <ul className="navbar-nav" style={{ flex: 1 }}>
              {children}
            </ul>
          </div>
          {!profileSwitcherOpened && (
            <ToggleMenuButton onClick={() => toggleNavigation(!compact)}>
              <i className={`fe fe-chevron-${compact ? 'right' : 'left'}`} />
            </ToggleMenuButton>
          )}
        </Container>
      </NavigationWrapper>
    </NavigationContext.Provider>
  );
};

Navigation.Link = function Link({
  to = '',
  name,
  icon,
  renderIcon,
  children,
  subItem,
  active = true,
  guard = () => true,
  id,
  className,
  end = false,
  activePaths = [],
  ...restProps
}) {
  const { compact, activeSection, setActiveSection } = useContext(NavigationContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const passedGuardCheck = guard();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (activeSection === id || activeSection?.startsWith(`${id}.`)) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [activeSection, id]);

  if (!passedGuardCheck) {
    return null;
  }

  const isActiveSection = activeSection === id || activeSection?.startsWith(`${id}.`) || false;
  const isActivePath = activePaths.some((activePath) =>
    matchesActivePath(activePath, location.pathname),
  );

  const handleClick = () => {
    if (children) {
      setIsExpanded(!isExpanded);
    }
    setActiveSection(id);
    if (to) {
      navigate(to);
    }
  };

  if (children) {
    return (
      <Nav.Item className={cx({ dropright: compact }, className)} {...restProps}>
        <Nav.Link
          className={cx('nav-link', { collapsed: !isExpanded })}
          style={isActiveSection ? { color: 'white' } : {}}
          role="button"
          aria-expanded={isExpanded}
          aria-controls={`sidebar-${id}`}
          onClick={handleClick}
        >
          {renderIcon ? renderIcon() : <i className={`fe ${icon}`} />}
          {!compact && (
            <NavigationLinkLabel $compact={compact} $subItem={subItem} $active={isActiveSection}>
              {name}
            </NavigationLinkLabel>
          )}
        </Nav.Link>
        {isExpanded && !compact && (
          <ul id={`sidebar-${id}`} className="nav nav-sm flex-column">
            {children}
          </ul>
        )}
        {compact && (
          <ul className="dropdown-menu">
            <li className="dropdown-header d-none d-md-block">
              <h6 className="text-uppercase mb-0">{name}</h6>
            </li>
            {children}
          </ul>
        )}
      </Nav.Item>
    );
  }

  return (
    <Nav.Item className={cx({ dropright: compact || subItem }, className)} {...restProps}>
      <NavLink
        className={({ isActive }) =>
          cx('nav-link', { active: active && (isActive || isActivePath) })
        }
        to={to}
        end={end || to === '/'}
        onClick={() => setActiveSection(id)}
      >
        {renderIcon ? renderIcon() : <i className={`fe ${icon}`} />}
        {(!compact || subItem) && (
          <NavigationLinkLabel $compact={compact} $subItem={subItem}>
            {name}
          </NavigationLinkLabel>
        )}
      </NavLink>
      {!subItem && compact && (
        <ul className="dropdown-menu">
          <li className="dropdown-header d-none d-md-block">
            <h6 className="text-uppercase mb-0">{name}</h6>
          </li>
        </ul>
      )}
    </Nav.Item>
  );
};

Navigation.propTypes = {
  toggleNavigation: PropTypes.func,
  compact: PropTypes.bool,
  profileSwitcherOpened: PropTypes.bool,
  children: PropTypes.node,
  items: PropTypes.array,
};

Navigation.Link.propTypes = {
  to: PropTypes.string,
  name: PropTypes.string.isRequired,
  icon: PropTypes.string,
  renderIcon: PropTypes.func,
  children: PropTypes.node,
  subItem: PropTypes.bool,
  active: PropTypes.bool,
  guard: PropTypes.func,
  id: PropTypes.string,
  className: PropTypes.string,
  end: PropTypes.bool,
  activePaths: PropTypes.array,
};

export default Navigation;
